import { BikeConfig } from '../config';

/**
 * Engine, automatic clutch and 5-speed gearbox, reduced to what the rear wheel feels: a torque and the engine
 * inertia reflected through the gear when the clutch is in. Gear ratios and shift points are the audio model's
 * (src/audio/model.ts DEFAULT_ENGINE), so the sound follows the same gear the physics is in.
 */
export interface EngineState {
  rpm: number;
  gear: number;        // 1..gearRatios.length
  shiftTimer: number;  // s of torque cut left from the last shift
  clutch: number;      // 1 = engaged, < 1 slipping (launch), 0 = pulled
}

export const createEngine = (cfg: BikeConfig): EngineState => ({ rpm: cfg.idleRpm, gear: 1, shiftTimer: 0, clutch: 0 });

const RAD_TO_RPM = 60 / (2 * Math.PI);

/** Fraction of peak torque at an rpm, linear between the curve points, flat outside them. */
export function torqueFraction(rpm: number, cfg: BikeConfig): number {
  const c = cfg.torqueCurve;
  if (rpm <= c[0]![0]) return c[0]![1];
  for (let i = 1; i < c.length; i++) {
    const p1 = c[i]!, r1 = p1[0], f1 = p1[1]; // (no destructuring: array destructuring allocates an iterator)
    if (rpm <= r1) {
      const p0 = c[i - 1]!, r0 = p0[0], f0 = p0[1];
      return f0 + ((f1 - f0) * (rpm - r0)) / (r1 - r0);
    }
  }
  return c[c.length - 1]![1];
}

/** Highest rpm (fraction of idle..limiter) a throttle opening can hold (see partThrottleLoss). */
export const throttleRpmFraction = (throttle: number, cfg: BikeConfig): number =>
  Math.min(1, (Math.min(1, Math.max(0, throttle)) * (1 + cfg.partThrottleLoss)) / Math.max(1e-6, cfg.partThrottleLoss));

/**
 * Where the automatic box shifts for a throttle. Wide open it uses the full shiftUpRpm / shiftDownRpm (the audio
 * model's points); at part throttle it shifts up before the revs the throttle can hold, and lets the revs sag lower
 * before shifting down, the way a rider short-shifts when cruising. Always at least 25 % apart after a shift up, so
 * it never hunts.
 */
export function shiftPoints(throttle: number, cfg: BikeConfig, out = { up: 0, down: 0 }): { up: number; down: number } {
  const t = Math.min(1, Math.max(0, throttle));
  const span = cfg.limiterRpm - cfg.idleRpm;
  const upFrac = Math.min((cfg.shiftUpRpm - cfg.idleRpm) / span, Math.max(0.35, 0.9 * throttleRpmFraction(t, cfg)));
  const up = cfg.idleRpm + upFrac * span;
  const lug = cfg.idleRpm + 0.6 * (cfg.shiftDownRpm - cfg.idleRpm);
  out.up = up;
  out.down = Math.min(lug + (cfg.shiftDownRpm - lug) * t, up * 0.6);
  return out;
}

export interface DriveOutput {
  torque: number;   // N*m at the rear wheel, + drives forward
  inertia: number;  // kg*m^2 of engine reflected onto the rear wheel (0 when the clutch is out or slipping)
}

/**
 * One step. `wheelOmega` is the rear wheel spin (rad/s, + rolling forward), `roadOmega` the road speed over the
 * radius. Shifts only with a wheel on the ground (in the air the gear holds, like the audio model). A rear lever past clutchPullRearBrake pulls the
 * clutch: no drive, no engine braking, and the wheel alone carries its spin (what a rider does before a
 * rear-brake tap in the air).
 */
const PTS = { up: 0, down: 0 };

/** Writes into `out` (reused by the 240 Hz step: no allocation) and returns it. */
export function stepDrivetrain(e: EngineState, throttle: number, rearLever: number, wheelOmega: number, grounded: boolean,
  cfg: BikeConfig, dt: number, roadOmega: number = wheelOmega, out: DriveOutput = { torque: 0, inertia: 0 }): DriveOutput {
  e.shiftTimer = Math.max(0, e.shiftTimer - dt);
  const ratios = cfg.gearRatios;
  // Shift on road speed (roadOmega = speed / R), not on a spinning wheel: wheelspin in 1st does not climb the box,
  // it runs into the limiter, which cuts the drive (the only traction control a rider has).
  if (grounded && e.shiftTimer <= 0) {
    shiftPoints(throttle, cfg, PTS);
    const road = Math.abs(roadOmega) * RAD_TO_RPM * ratios[e.gear - 1]!;
    if (road > PTS.up && e.gear < ratios.length) { e.gear++; e.shiftTimer = cfg.shiftTime; }
    else if (road < PTS.down && e.gear > 1) { e.gear--; e.shiftTimer = cfg.shiftTime; }
  }
  const ratio = ratios[e.gear - 1]!;
  const wheelRpm = Math.abs(wheelOmega) * RAD_TO_RPM * ratio;
  const t = Math.min(1, Math.max(0, throttle));
  const gain = ratio * cfg.driveEfficiency;

  // On the ground a hard rear lever pulls the clutch (no stall). In the air the rider keeps it in: a rear-brake tap
  // stops the wheel AND the crank behind it, which is why the rear brake is the strong nose-down tool (row 13).
  if (rearLever > cfg.clutchPullRearBrake && grounded) {
    e.clutch = 0;
    e.rpm += (cfg.idleRpm + t * (cfg.limiterRpm - cfg.idleRpm) - e.rpm) * Math.min(1, 8 * dt);
    out.torque = 0; out.inertia = 0;
    return out;
  }
  if (e.gear === 1 && wheelRpm < cfg.launchRpm) {
    // Launch: the clutch slips and holds the engine near launch rpm; it passes the engine's torque there.
    e.clutch = wheelRpm / cfg.launchRpm;
    const target = Math.max(wheelRpm, cfg.idleRpm + t * (cfg.launchRpm - cfg.idleRpm));
    e.rpm += (target - e.rpm) * Math.min(1, 10 * dt);
    out.torque = t * cfg.engineTorque * torqueFraction(e.rpm, cfg) * gain; out.inertia = 0;
    return out;
  }
  e.clutch = 1;
  e.rpm = Math.max(cfg.idleRpm, wheelRpm);
  out.inertia = cfg.engineInertia * ratio * ratio;
  if (e.shiftTimer > 0) { out.torque = 0; return out; }
  const rpmFrac = Math.min(1, Math.max(0, (e.rpm - cfg.idleRpm) / (cfg.limiterRpm - cfg.idleRpm)));
  // A part-open throttle cannot feed high rpm: the air it lets through runs out as the revs rise, so a steady part
  // throttle settles at a part rpm instead of screaming to the limiter (in the air, or at a steady cruise).
  const open = Math.min(1, Math.max(0, t * (1 + cfg.partThrottleLoss) - cfg.partThrottleLoss * rpmFrac));
  const drive = e.rpm >= cfg.limiterRpm ? 0 : open * cfg.engineTorque * torqueFraction(e.rpm, cfg);
  // Engine braking grows with rpm above idle; the idle governor holds idle, so it never drags the engine below it.
  const braking = (1 - open) * cfg.engineBrake * rpmFrac * Math.sign(wheelOmega);
  out.torque = (drive - braking) * gain;
  return out;
}
